import { compose, withProps } from 'recompose'
import * as R from 'ramda'
import {
  doesBenefitExist,
  formatPrice,
  getBenefitLimitValue,
  getPendingPolicyStatus,
  getPolicyStatus,
} from 'sidecar-utility-library'

import { formatPolicyCost, getPrescriptionsTaken } from './utils'

const enhance = compose(
  // Policy banner
  withProps(({ policy = {} }) => {
    const { pendingChange = {} } = policy
    const pendingPolicyStatus = getPendingPolicyStatus(policy)

    return {
      /* This is setting up a simple interface of 
    [property]: {
      pending: 
      current: 
    }

    so that the UI has a more consistent way to deal with
    all these different types of values. The pending and current need to be
    completely preprocessed and ready for UI in their final format
    
    */
      cost: {
        // We need to use the paymentSchedule from policy since it doesnt come in to pendingChange
        pending: formatPolicyCost({
          cost: pendingChange.cost,
          paymentSchedule: policy.paymentSchedule,
        }),
        current: formatPolicyCost(policy),
      },
      monthlyCost: {
        // if null, return null, otherwise process like usual
        pending: R.ifElse(
          R.isNil,
          R.always(null),
          monthlyCost => `${formatPrice(monthlyCost)}/mo`,
        )(pendingChange.monthlyCost),
        current: `${formatPrice(policy.monthlyCost)}/mo`,
      },
      allowance: {
        pending: formatPrice(pendingChange.maxAnnualAllowance),
        current: formatPrice(getBenefitLimitValue(policy, 'ALLOWANCE')),
      },
      deductible: {
        pending: formatPrice(pendingChange.deductible),
        current: formatPrice(getBenefitLimitValue(policy, 'DEDUCTIBLE')),
      },
      prescriptionsCovered: {
        // if null, return null, otherwise process like usual
        pending: R.ifElse(R.isNil, R.always(null), prescriptionsCovered =>
          prescriptionsCovered ? 'Covered' : 'Not covered',
        )(pendingChange.prescriptionsCovered),
        current: doesBenefitExist(policy, 'PRESCRIPTIONS_COVERED')
          ? 'Covered'
          : 'Not covered',
      },
      prescriptionsTaken: {
        pending: getPrescriptionsTaken(pendingChange),
        current: getPrescriptionsTaken(policy),
      },
      procedures: {
        // You cant do a R.pathOr here because it checks for R.isNil not R.isEmpty
        pending:
          R.path(['medicalHistory', 'procedures'], pendingChange) || 'Nothing',
        current: R.path(['medicalHistory', 'procedures'], policy) || 'Nothing',
      },
      voluntaryExclusion: {
        // You cant do a R.pathOr here because it checks for R.isNil not R.isEmpty
        pending:
          R.path(['medicalHistory', 'voluntaryExclusion'], pendingChange) ||
          'Nothing',
        current:
          R.path(['medicalHistory', 'voluntaryExclusion'], policy) || 'Nothing',
      },
      // Using MATERNITY_ALLOWANCE existence because backend sometimes doesnt send
      // back MATERNITY_COVERED
      maternityCovered: {
        // if null, return null, otherwise process like usual
        pending: R.ifElse(R.isNil, R.always(null), maternityCovered =>
          maternityCovered ? 'Covered' : 'Not covered',
        )(pendingChange.maternityCovered),
        current: doesBenefitExist(policy, 'MATERNITY_ALLOWANCE')
          ? 'Covered'
          : 'Not covered',
      },
      maternityAllowance: {
        pending: formatPrice(pendingChange.maternityAllowance),
        current: formatPrice(
          getBenefitLimitValue(policy, 'MATERNITY_ALLOWANCE') || 0,
        ),
      },
      maternityDeductible: {
        pending: formatPrice(pendingChange.maternityDeductible),
        // pending: R.ifElse(
        //   R.isNil,
        //   R.always(null),
        //   formatPrice,
        // )(pendingChange.maternityDeductible),
        current: formatPrice(
          getBenefitLimitValue(policy, 'MATERNITY_DEDUCTIBLE') || 0,
        ),
      },
      threeYearRateLock: {
        // if null, return null, otherwise process like usual
        pending: R.ifElse(R.isNil, R.always(null), threeYearRateLock =>
          threeYearRateLock ? 'Enabled' : 'Not enabled',
        )(pendingChange.threeYearRateLock),
        current: doesBenefitExist(policy, 'THREE_YEAR_RATE_LOCK')
          ? 'Enabled'
          : 'Not enabled',
      },
      denyReason: {
        // You cant do a R.pathOr here because it checks for R.isNil not R.isEmpty
        pending:
          pendingPolicyStatus === 'PENDING_ACCEPTANCE'
            ? R.path(['medicalHistory', 'denyReason'], pendingChange) || '-'
            : null,
        current: R.path(['medicalHistory', 'denyReason'], policy) || '-',
      },

      hasPolicyPendingChanges: !R.isEmpty(pendingChange),
      isPendingAcceptance: pendingPolicyStatus === 'PENDING_ACCEPTANCE',
      isPendingApproval: pendingPolicyStatus === 'PENDING_APPROVAL',
      isCanceledOrExpired: ['CANCELLED', 'EXPIRED'].includes(
        getPolicyStatus(policy),
      ),
    }
  }),
)

export default enhance
